/**
 * 거래 모니터(EA-29) — 계약 XM-53(page).
 *
 * ⚠ **읽기 전용이다.** 여기서는 아무것도 처리하지 않고 링크도 없다(레거시 그대로) —
 * 사고는 「사고 처리」 화면에서 다룬다.
 *
 * ⚠ **진행 중인 거래도 나온다.** 계약 거르개가 한 단계만 받아 레거시의 '체결된 거래만'을
 * 못 만든다(명세 7절 C-09). 순서는 서버 그대로(지금 코드: 매칭 새것부터).
 *
 * ⚠ **금액은 계산하지 않는다.** 내부 단위 문자열을 `formatUsde` 로 글만 만든다(M-05).
 */
import { useQuery } from '@tanstack/react-query'
import { listTrades } from '@/api/admin'
import { Card, Labeled, Maybe, NUM, TD, TH, Time } from '@/components/ui'
import { QueryState } from '@/components/QueryState'
import { PagePager } from '@/components/Pager'
import { useFilters } from '@/components/Filters'
import { usde } from '@/format'
import { TRADE_SOURCE_LABEL, TRADE_STAGE_LABEL } from '@/api/types'

const SIZE = 20
const NO_FILTERS: readonly string[] = []

/** 회원 번호만 온다(7절 C-04) — 링크 없이 「회원 #번호」. */
function member(id: number | null): string | null {
  return id === null ? null : `Member #${id}`
}

export function TradesPage() {
  const f = useFilters(NO_FILTERS)
  const params = { page: f.page, size: SIZE }
  const q = useQuery({ queryKey: ['trades', params], queryFn: () => listTrades(params) })
  const rows = q.data?.items ?? []

  return (
    <Card title="Trade monitor">
      <p className="mb-4 text-xs text-slate-500">
        Shows all trades, newest match first. Trades in progress are included. Read only.
      </p>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="There are no trades.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Source</th><th className={TH}>Type</th>
            <th className={TH}>Quantity</th><th className={TH}>Fee</th><th className={TH}>Stage</th>
            <th className={TH}>General member → Call</th><th className={TH}>Matched at</th>
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.orderId}>
                <td className={`${TD} font-mono text-xs`}>{t.orderId}</td>
                <td className={TD}><Labeled labels={TRADE_SOURCE_LABEL} code={t.source} /></td>
                {/* `start_sell` = 일반 회원이 삼 = 구매(붉은색), `start_buy` = 판매(청록색). */}
                <td className={`${TD} font-medium ${t.depositType === 'start_sell'
                  ? 'text-rose-600 dark:text-rose-400'
                  : t.depositType === 'start_buy' ? 'text-cyan-700 dark:text-cyan-400' : ''}`}>
                  {t.depositType === 'start_sell' ? 'Buy' : t.depositType === 'start_buy' ? 'Sell' : t.depositType}
                </td>
                <td className={NUM}>{usde(t.amount)}</td>
                <td className={NUM}><Maybe value={usde(t.fee)} /></td>
                <td className={TD}><Labeled labels={TRADE_STAGE_LABEL} code={t.stage} /></td>
                <td className={TD}>
                  <Maybe value={member(t.generalMemberId)} /> → <Maybe value={member(t.callMemberId)} />
                </td>
                <td className={TD}><Time ms={t.matchedAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>

      <PagePager page={f.page} size={SIZE} total={q.data?.total} onChange={f.setPage} withPrev always />
    </Card>
  )
}
